// Make changes › More actions › Who can do what — plain words ⇄ the model:
//   * Who sees which records → the header form's `row_security`
//     ({rules: [{column, source: 'user_id'}], exempt_roles});
//   * Lock records when…     → `list_view.edit_lock` ({expression, reason});
//   * Filters people must use → `list_view.enforced_filter_sets` (one set).
// Pure functions. A shape these words cannot describe reads back as
// `unreadable: true` and is left alone (the full designer owns it).

import type { EditLockRule, EnforcedCondition, EnforcedFilterSet, RowSecurity } from '../../txn/types'
import { TEXT_LIT, quoteText, unquoteText } from './itemChange'

// ── Who sees which records ───────────────────────────────────────────────

export interface OwnRecords {
  on: boolean
  /** The field holding the person a record belongs to. */
  column: string
  /** Roles that still see every record. */
  exempt: string[]
  unreadable?: boolean
}

export function readRowSecurity(rs: RowSecurity | null | undefined): OwnRecords {
  if (!rs || (!rs.column && !(rs.rules || []).length)) return { on: false, column: '', exempt: [] }
  const rules = rs.rules?.length ? rs.rules : [{ column: rs.column || '', source: rs.source, op: rs.op, sql: rs.sql }]
  if (rules.length !== 1 || (rules[0].source || 'user_id') !== 'user_id' || (rules[0].op && rules[0].op !== 'eq') || rules[0].sql) {
    return { on: true, column: rules[0]?.column || '', exempt: rs.exempt_roles || [], unreadable: true }
  }
  return { on: true, column: rules[0].column, exempt: rs.exempt_roles || [] }
}

export function writeRowSecurity(v: OwnRecords): RowSecurity | null {
  if (!v.on || !v.column) return null
  return { rules: [{ column: v.column, source: 'user_id' }], ...(v.exempt.length ? { exempt_roles: v.exempt } : {}) }
}

// ── Lock records when… ───────────────────────────────────────────────────

export interface LockWhen {
  field: string
  value: string
  reason: string
  unreadable?: boolean
}

const LOCK_RE = new RegExp(`^\\{form\\.([A-Za-z_][A-Za-z0-9_]*)\\}\\s*==\\s*(${TEXT_LIT})$`)

export function readEditLock(lock: EditLockRule | null | undefined): LockWhen | null {
  const expr = String(lock?.expression || '').trim()
  if (!expr || lock?.enabled === false) return null
  const m = expr.match(LOCK_RE)
  if (!m) return { field: '', value: '', reason: lock?.reason || '', unreadable: true }
  return { field: m[1], value: unquoteText(m[2]) ?? '', reason: lock?.reason || '' }
}

export function writeEditLock(v: LockWhen | null): EditLockRule | null {
  if (!v || !v.field || !v.value.trim()) return null
  return {
    expression: `{form.${v.field}} == ${quoteText(v.value.trim())}`,
    reason: v.reason.trim() || 'This record can’t be changed any more.',
  }
}

// ── Filters people must use ──────────────────────────────────────────────

export interface RequiredFilter {
  column: string
  /** A starting value ('' = people choose). */
  value: string
  /** People may not change it. */
  locked: boolean
  /** A date field: a from–to range. */
  range: boolean
}

export const FILTER_SET_ID = 'required'

export function readFilters(sets: EnforcedFilterSet[] | null | undefined): { filters: RequiredFilter[]; unreadable: boolean } {
  const list = sets || []
  if (!list.length) return { filters: [], unreadable: false }
  if (list.length > 1) return { filters: [], unreadable: true }
  const out: RequiredFilter[] = []
  for (const c of list[0].conditions || []) {
    if (c.op === 'between') {
      out.push({ column: c.column, value: c.default_from || '', locked: !!c.lock_values, range: true })
    } else if (c.op === 'eq') {
      out.push({ column: c.column, value: c.default_value || '', locked: !!c.lock_values, range: false })
    } else {
      return { filters: [], unreadable: true }
    }
  }
  return { filters: out, unreadable: false }
}

export function writeFilters(filters: RequiredFilter[], name = 'Required'): EnforcedFilterSet[] | null {
  const conds: EnforcedCondition[] = filters.filter((f) => f.column).map((f) => (f.range
    ? { column: f.column, op: 'between', ...(f.value ? { default_from: f.value } : {}), ...(f.locked ? { lock_values: true } : {}) }
    : { column: f.column, op: 'eq', ...(f.value ? { default_value: f.value } : {}), ...(f.locked ? { lock_values: true } : {}) }))
  if (!conds.length) return null
  return [{ id: FILTER_SET_ID, name, conditions: conds, is_default: true }]
}
